import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { studentAPI, narrativeAPI, narrativeProgressAPI } from '@/lib/api';
import GuardianWritingPanel from '@/components/guardian/GuardianWritingPanel';
import ResearchConsentBox from '@/components/guardian/ResearchConsentBox';
import { toast } from 'sonner';
import { BrutalCard, BrutalButton, BrutalBadge } from '@/components/brutal';
import { TrendingUp, BookOpen, Clock, Target, ChevronLeft, ChevronDown, ChevronUp, Award, BarChart3, Brain, Download, Printer, CalendarDays, Timer, Activity, Eye, X, CheckCircle, Circle } from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend,
  AreaChart, Area
} from 'recharts';

const CHART_COLORS = ['#6366f1', '#f59e0b', '#10b981', '#ef4444', '#8b5cf6', '#ec4899'];

const StatCard = ({ label, value, sub, icon: Icon, color = 'indigo' }) => {
  const bgMap = { indigo: 'bg-indigo-50', amber: 'bg-amber-50', emerald: 'bg-emerald-50', rose: 'bg-rose-50' };
  const textMap = { indigo: 'text-indigo-600', amber: 'text-amber-600', emerald: 'text-emerald-600', rose: 'text-rose-600' };
  return (
    <div className={`${bgMap[color]} border-4 border-black p-5 brutal-shadow-sm`} data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="flex items-center gap-2 mb-2">
        {Icon && <Icon size={18} className={textMap[color]} />}
        <p className="font-bold text-xs uppercase text-gray-600">{label}</p>
      </div>
      <p className="text-3xl font-black">{value}</p>
      {sub && <p className="text-sm font-medium text-gray-500 mt-1">{sub}</p>}
    </div>
  );
};

const EmptyState = ({ message }) => (
  <div className="text-center py-10 border-4 border-dashed border-gray-300 bg-gray-50">
    <BarChart3 size={40} className="mx-auto mb-3 text-gray-400" />
    <p className="text-lg font-bold text-gray-500">{message}</p>
    <p className="text-sm text-gray-400 mt-1">Data will appear here once the student starts learning</p>
  </div>
);

const formatTime = (seconds) => {
  if (!seconds) return '0m';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
};

const StoryStatsCard = ({ story, onRead, onArchive, onDelete }) => {
  const [expanded, setExpanded] = useState(false);
  const [detailedStats, setDetailedStats] = useState(null);
  const [loadingStats, setLoadingStats] = useState(false);

  const handleExpand = async () => {
    if (expanded) {
      setExpanded(false);
      return;
    }
    setExpanded(true);
    if (!detailedStats) {
      setLoadingStats(true);
      try {
        const res = await narrativeProgressAPI.getStats(story.id);
        setDetailedStats(res.data);
      } catch (err) {
        // Fall back to inline stats
      }
      setLoadingStats(false);
    }
  };

  const completionPct = story.chapters_total > 0 ? Math.round((story.chapters_completed / story.chapters_total) * 100) : 0;
  const isArchived = story.status === 'archived';

  return (
    <div className={`border-4 ${isArchived ? 'bg-gray-200 border-gray-400 opacity-60' : 'bg-white border-black'}`} data-testid={`story-${story.id}`}>
      {/* Header Row */}
      <div className="p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <p className={`font-black text-lg truncate ${isArchived ? 'line-through text-gray-500' : ''}`}>{story.title}</p>
              {isArchived && (
                <span className="text-[10px] font-black uppercase tracking-wider px-2 py-0.5 bg-gray-500 text-white rounded flex-shrink-0">ARCHIVED</span>
              )}
              <BrutalBadge variant={story.status === 'completed' ? 'emerald' : isArchived ? 'gray' : 'amber'} size="sm">
                {story.status}
              </BrutalBadge>
            </div>
            <p className="text-xs text-gray-400 font-medium">
              Created: {story.created_date ? new Date(story.created_date).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'N/A'}
              {story.last_read_date ? ` · Last read: ${new Date(story.last_read_date).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}` : ' · Not read yet'}
            </p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <button onClick={() => {
              const token = localStorage.getItem('token');
              const url = narrativeProgressAPI.getReportUrl(story.id);
              fetch(url, { headers: { Authorization: `Bearer ${token}` } })
                .then(res => res.text())
                .then(html => { const w = window.open('', '_blank'); w.document.write(html); w.document.close(); });
            }} className="text-xs font-bold px-3 py-1.5 bg-violet-500 text-white rounded hover:bg-violet-600 transition-colors flex items-center gap-1">
              <Printer size={12} /> Print
            </button>
            <button onClick={onRead} className="text-xs font-bold px-3 py-1.5 bg-indigo-500 text-white rounded hover:bg-indigo-600 transition-colors flex items-center gap-1">
              <Eye size={12} /> Read
            </button>
            <button onClick={onArchive} className={`text-xs font-bold px-3 py-1.5 text-white rounded transition-colors ${isArchived ? 'bg-emerald-500 hover:bg-emerald-600' : 'bg-gray-700 hover:bg-gray-800'}`}>
              {isArchived ? 'Restore' : 'Archive'}
            </button>
            <button onClick={onDelete} className="text-xs font-bold px-3 py-1.5 bg-red-600 text-white rounded hover:bg-red-700 transition-colors">Delete</button>
          </div>
        </div>

        {/* Inline Stats Bar */}
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3 mt-3">
          <div className="text-center">
            <p className="text-[10px] font-bold uppercase text-gray-500">Progress</p>
            <p className="text-lg font-black">{story.chapters_completed}/{story.chapters_total}</p>
            <div className="w-full h-1.5 rounded-full bg-gray-200 mt-1">
              <div className="h-1.5 rounded-full bg-indigo-500 transition-all" style={{ width: `${completionPct}%` }} />
            </div>
          </div>
          <div className="text-center">
            <p className="text-[10px] font-bold uppercase text-gray-500">Reading Time</p>
            <p className="text-lg font-black">{formatTime(story.reading_time_seconds || 0)}</p>
          </div>
          <div className="text-center">
            <p className="text-[10px] font-bold uppercase text-gray-500">Avg WPM</p>
            <p className="text-lg font-black">{story.average_wpm || '—'}</p>
          </div>
          <div className="text-center">
            <p className="text-[10px] font-bold uppercase text-gray-500">Words Read</p>
            <p className="text-lg font-black">{(story.words_read || 0).toLocaleString()}</p>
          </div>
          <div className="text-center">
            <p className="text-[10px] font-bold uppercase text-gray-500">Vocab Mastered</p>
            <p className="text-lg font-black">{story.tokens_mastered || 0}<span className="text-xs text-gray-400 font-medium">/{story.tokens_encountered || 0}</span></p>
          </div>
          <div className="text-center">
            <p className="text-[10px] font-bold uppercase text-gray-500">Assessment</p>
            <p className="text-lg font-black">{story.assessment_accuracy || 0}%</p>
          </div>
        </div>

        {/* Testable-word coverage badge. Only renders when we know the
            grade-scaled target AND the story falls below it — silent for
            stories that hit the minimum. */}
        {story.expected_min_questions > 0 && story.testable_count > 0 && story.bank_too_thin && (
          <div className="mt-3 flex items-center gap-2 px-3 py-1.5 rounded-md bg-amber-50 border-2 border-amber-300" data-testid={`thin-bank-badge-${story.id}`}>
            <span className="text-[11px] font-black text-amber-700">
              {story.testable_count}/{story.expected_min_questions} testable
            </span>
            <span className="text-[11px] font-medium text-amber-700">
              — bank too thin for this grade; assessment is shorter than the grade-level minimum.
            </span>
          </div>
        )}

        {/* Expand Button */}
        <button onClick={handleExpand} className="w-full mt-3 pt-2 border-t-2 border-gray-200 flex items-center justify-center gap-1 text-xs font-bold text-indigo-500 hover:text-indigo-700 transition-colors">
          {expanded ? <><ChevronUp size={14} /> Hide Chapter Details</> : <><ChevronDown size={14} /> View Chapter Details</>}
        </button>
      </div>

      {/* Expanded Chapter-by-Chapter Details */}
      {expanded && (
        <div className="border-t-4 border-black p-4 bg-gray-50">
          {loadingStats ? (
            <p className="text-center text-gray-500 font-bold py-4">Loading detailed stats...</p>
          ) : detailedStats ? (
            <div className="space-y-4">
              {/* Overall Story Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="bg-indigo-50 border-2 border-black p-3">
                  <p className="text-[10px] font-bold uppercase text-gray-500">Completion</p>
                  <p className="text-2xl font-black">{detailedStats.progress.completion_percentage}%</p>
                </div>
                <div className="bg-emerald-50 border-2 border-black p-3">
                  <p className="text-[10px] font-bold uppercase text-gray-500">Vocab Mastery</p>
                  <p className="text-2xl font-black">{detailedStats.vocabulary.mastery_rate}%</p>
                  <p className="text-xs text-gray-500">{detailedStats.vocabulary.tokens_mastered_count} of {detailedStats.vocabulary.tokens_encountered_count} words</p>
                </div>
                <div className="bg-amber-50 border-2 border-black p-3">
                  <p className="text-[10px] font-bold uppercase text-gray-500">Vision Checks</p>
                  <p className="text-2xl font-black">{detailedStats.vision_checks.passed}/{detailedStats.vision_checks.total}</p>
                </div>
                <div className="bg-rose-50 border-2 border-black p-3">
                  <p className="text-[10px] font-bold uppercase text-gray-500">Assessment Avg</p>
                  <p className="text-2xl font-black">{detailedStats.assessments.average_accuracy}%</p>
                  <p className="text-xs text-gray-500">{detailedStats.assessments.completed} completed</p>
                </div>
              </div>

              {/* Chapter Breakdown with Q&A Detail */}
              <div>
                <h4 className="font-black text-sm uppercase text-gray-600 mb-2">Chapter-by-Chapter Progress</h4>
                <div className="space-y-3">
                  {detailedStats.chapters.map((ch) => (
                    <div key={ch.chapter_number} className={`border-2 ${ch.is_completed ? 'border-emerald-400 bg-emerald-50' : 'border-gray-300 bg-white'}`}>
                      {/* Chapter header */}
                      <div className="flex items-center gap-3 p-3">
                        <div className="flex-shrink-0">
                          {ch.is_completed ? (
                            <CheckCircle size={20} className="text-emerald-500" />
                          ) : (
                            <Circle size={20} className="text-gray-300" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-bold text-sm truncate">Ch. {ch.chapter_number}: {ch.title}</p>
                          <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1">
                            <span className="text-xs text-gray-500"><Clock size={10} className="inline mr-1" />{formatTime(ch.reading_time_seconds)}</span>
                            <span className="text-xs text-gray-500"><BookOpen size={10} className="inline mr-1" />{ch.average_wpm || '—'} WPM</span>
                            <span className="text-xs text-gray-500"><Brain size={10} className="inline mr-1" />{ch.tokens_encountered.length} vocab</span>
                            <span className="text-xs text-gray-500"><Eye size={10} className="inline mr-1" />Vision: {ch.vision_check_passed ? '✓' : '—'}</span>
                            {ch.times_read > 1 && <span className="text-xs text-indigo-500 font-bold">Read {ch.times_read}x</span>}
                          </div>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <p className="text-xs font-bold text-gray-500">{ch.word_count} words</p>
                        </div>
                      </div>

                      {/* Comprehension Q&A for this chapter */}
                      {ch.written_answers && ch.written_answers.length > 0 && (
                        <div className="px-3 pb-3 pt-1 border-t border-gray-200 ml-8">
                          <p className="text-[10px] font-black uppercase text-gray-400 mb-2">Comprehension Answers</p>
                          {ch.written_answers.map((wa, idx) => (
                            <div key={idx} className={`p-2.5 mb-2 border-l-4 ${wa.passed ? 'border-emerald-400 bg-emerald-50/50' : 'border-red-400 bg-red-50/50'}`}>
                              <p className="text-xs font-bold text-gray-700 mb-1">Q: {wa.question}</p>
                              <p className="text-xs text-gray-600 mb-1">A: <span className="font-medium italic">"{wa.student_answer}"</span></p>
                              <div className="flex items-center gap-3 mt-1">
                                <span className={`text-[10px] font-black uppercase px-2 py-0.5 rounded ${wa.passed ? 'bg-emerald-200 text-emerald-700' : 'bg-red-200 text-red-700'}`}>
                                  {wa.passed ? 'PASSED' : 'NOT PASSED'}
                                </span>
                                {wa.comprehension_score > 0 && (
                                  <span className="text-[10px] font-bold text-gray-500">Score: {wa.comprehension_score}/100</span>
                                )}
                              </div>
                              {wa.feedback && <p className="text-[11px] text-gray-500 mt-1 italic">{wa.feedback}</p>}
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Spelling errors for this chapter */}
                      {ch.spelling_errors && ch.spelling_errors.length > 0 && (
                        <div className="px-3 pb-3 pt-1 border-t border-gray-200 ml-8">
                          <p className="text-[10px] font-black uppercase text-gray-400 mb-2">Spelling Errors</p>
                          <div className="flex flex-wrap gap-2">
                            {ch.spelling_errors.map((err, idx) => (
                              <span key={idx} className="text-xs bg-amber-100 border border-amber-300 px-2 py-1 rounded">
                                <span className="line-through text-red-500">{err.written}</span> → <span className="font-bold text-emerald-700">{err.correct}</span>
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Vocabulary Assessment Detail */}
              {detailedStats.assessments.history.length > 0 && (
                <div>
                  <h4 className="font-black text-sm uppercase text-gray-600 mb-2">Vocabulary Assessment Results</h4>
                  {detailedStats.assessments.history.map((assess, aIdx) => (
                    <div key={aIdx} className="border-2 border-black mb-3 bg-white">
                      <div className="flex items-center justify-between p-3 bg-indigo-50 border-b-2 border-black">
                        <div>
                          <p className="font-bold text-sm">Assessment #{detailedStats.assessments.history.length - aIdx}</p>
                          <p className="text-xs text-gray-500">{assess.type} · {assess.date ? new Date(assess.date).toLocaleDateString() : ''}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-lg font-black">{assess.accuracy}%</p>
                          <p className="text-xs text-gray-500">{assess.correct}/{assess.total} correct</p>
                        </div>
                      </div>
                      {assess.questions && assess.questions.length > 0 && (
                        <div className="p-3 space-y-2">
                          {assess.questions.map((q, qIdx) => (
                            <div key={qIdx} className={`p-2.5 border-l-4 ${q.is_correct ? 'border-emerald-400 bg-emerald-50/50' : 'border-red-400 bg-red-50/50'}`}>
                              <div className="flex items-start justify-between gap-2">
                                <p className="text-xs font-black text-gray-700">{q.word}</p>
                                <span className={`text-[10px] font-black uppercase px-2 py-0.5 rounded flex-shrink-0 ${q.is_correct ? 'bg-emerald-200 text-emerald-700' : 'bg-red-200 text-red-700'}`}>
                                  {q.is_correct ? 'CORRECT' : 'INCORRECT'}
                                </span>
                              </div>
                              {q.student_definition && (
                                <p className="text-xs text-gray-600 mt-1">Definition: <span className="italic">"{q.student_definition}"</span></p>
                              )}
                              {q.student_sentence && (
                                <p className="text-xs text-gray-600 mt-0.5">Sentence: <span className="italic">"{q.student_sentence}"</span></p>
                              )}
                              {q.student_answer && !q.student_definition && (
                                <p className="text-xs text-gray-600 mt-1">Answer: <span className="italic">"{q.student_answer}"</span></p>
                              )}
                              {q.feedback && <p className="text-[11px] text-gray-500 mt-1 italic">{q.feedback}</p>}
                            </div>
                          ))}
                        </div>
                      )}
                      {assess.misspelled_words && assess.misspelled_words.length > 0 && (
                        <div className="px-3 pb-3">
                          <p className="text-[10px] font-black uppercase text-gray-400 mb-1">Misspelled Words</p>
                          <div className="flex flex-wrap gap-2">
                            {assess.misspelled_words.map((m, mIdx) => (
                              <span key={mIdx} className="text-xs bg-amber-100 border border-amber-300 px-2 py-1 rounded">
                                <span className="line-through text-red-500">{m.misspelled_as}</span> → <span className="font-bold text-emerald-700">{m.word}</span>
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Mastered Vocabulary from this story */}
              {detailedStats.vocabulary.tokens_mastered.length > 0 && (
                <div>
                  <h4 className="font-black text-sm uppercase text-gray-600 mb-2">Words Mastered in This Story</h4>
                  <div className="flex flex-wrap gap-2">
                    {detailedStats.vocabulary.tokens_mastered.map((word, idx) => (
                      <span key={idx} className="px-3 py-1.5 bg-emerald-100 border-2 border-black font-bold text-sm">
                        {word}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            /* Fallback to inline stats if detailed endpoint fails */
            <div className="text-center text-gray-500 py-4">
              <p className="font-bold">Stats: {story.sessions_count || 0} sessions · {story.vision_checks_passed || 0} vision checks passed</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const StudentProgressDetail = ({ studentId, onBack }) => {
  const queryClient = useQueryClient();
  const [showArchived, setShowArchived] = useState(false);
  const [readingStory, setReadingStory] = useState(null);
  const [readingChapter, setReadingChapter] = useState(1);
  const { data: progress, isLoading } = useQuery({
    queryKey: ['student-progress', studentId],
    queryFn: async () => {
      const res = await studentAPI.getProgress(studentId);
      return res.data;
    },
    enabled: !!studentId,
  });

  const { data: timeLog, isLoading: timeLogLoading } = useQuery({
    queryKey: ['student-time-log', studentId],
    queryFn: async () => {
      const res = await studentAPI.getTimeLog(studentId);
      return res.data;
    },
    enabled: !!studentId,
  });

  const { data: goalProgress } = useQuery({
    queryKey: ['reading-goal-progress', studentId],
    queryFn: async () => (await studentAPI.getReadingGoalProgress(studentId)).data,
    enabled: !!studentId,
  });

  const handleExportJSON = () => {
    const token = localStorage.getItem('token');
    const url = studentAPI.getExportUrl(studentId, 'json');
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => res.blob())
      .then(blob => {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = `leximaster_report_${progress?.student?.full_name?.replace(/\s/g, '_') || studentId}.json`;
        a.click();
        URL.revokeObjectURL(a.href);
      });
  };

  const handleExportHTML = () => {
    const token = localStorage.getItem('token');
    const url = studentAPI.getExportUrl(studentId, 'html');
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => res.text())
      .then(html => {
        const w = window.open('', '_blank');
        w.document.write(html);
        w.document.close();
      });
  };

  if (isLoading) return <div className="text-center py-12 text-2xl font-bold">Loading progress...</div>;
  if (!progress) return <div className="text-center py-12 text-2xl font-bold">No data found</div>;

  const { student, reading_stats, vocabulary, assessments, narratives, assigned_banks } = progress;

  const masteryData = [
    { name: 'Mastered', value: vocabulary.mastered_count },
    { name: 'Remaining', value: Math.max(0, vocabulary.biological_target - vocabulary.mastered_count) },
  ];

  const assessmentChartData = assessments.history.map((a, i) => ({
    name: `#${assessments.history.length - i}`,
    accuracy: a.accuracy,
    correct: a.correct,
    total: a.total,
  })).reverse();

  const narrativeStatusData = [
    { name: 'Completed', value: narratives.completed },
    { name: 'In Progress', value: narratives.total - narratives.completed },
  ].filter(d => d.value > 0);

  return (
    <div className="space-y-6" data-testid="student-progress-detail">
      {/* Back + Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-4">
          <BrutalButton variant="default" size="sm" onClick={onBack} className="flex items-center gap-1" data-testid="progress-back-btn">
            <ChevronLeft size={18} /> Back
          </BrutalButton>
          <div>
            <h2 className="text-3xl font-black uppercase">{student.full_name}</h2>
            <p className="text-sm font-medium text-gray-500">
              {student.age ? `Age ${student.age}` : ''}{student.grade_level ? ` · Grade ${student.grade_level}` : ''}
            </p>
          </div>
          {student.agentic_reach_score > 0 && (
            <BrutalBadge variant="indigo" size="lg">Score: {student.agentic_reach_score}</BrutalBadge>
          )}
        </div>
        <div className="flex gap-2">
          <BrutalButton variant="emerald" size="sm" onClick={handleExportJSON} className="flex items-center gap-1" data-testid="export-json-btn">
            <Download size={16} /> Export JSON
          </BrutalButton>
          <BrutalButton variant="indigo" size="sm" onClick={handleExportHTML} className="flex items-center gap-1" data-testid="export-html-btn">
            <Printer size={16} /> Print Report
          </BrutalButton>
        </div>
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="Words Mastered" value={vocabulary.mastered_count} sub={`of ${vocabulary.biological_target} target`} icon={Brain} color="indigo" />
        <StatCard label="Reading Time" value={formatTime(reading_stats.total_reading_seconds)} sub={`${reading_stats.sessions_count} sessions`} icon={Clock} color="amber" />
        <StatCard label="Avg WPM" value={reading_stats.average_wpm || '—'} sub={`${reading_stats.total_words_read} total words`} icon={BookOpen} color="emerald" />
        <StatCard label="Assessments" value={assessments.completed} sub={`${assessments.average_accuracy}% avg accuracy`} icon={Award} color="rose" />
      </div>

      {/* Reading Goal Progress */}
      {goalProgress?.has_goal && (
        <div className={`border-4 p-5 ${goalProgress.is_on_track ? 'bg-emerald-50 border-emerald-500' : 'bg-red-50 border-red-500'}`} data-testid="reading-goal-banner">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Target size={20} className={goalProgress.is_on_track ? 'text-emerald-600' : 'text-red-600'} />
              <h3 className="text-lg font-black uppercase">Reading Goal</h3>
              <BrutalBadge variant={goalProgress.is_on_track ? 'emerald' : 'rose'} size="sm">
                {goalProgress.is_on_track ? 'ON TRACK' : 'BEHIND'}
              </BrutalBadge>
            </div>
            <p className="text-sm font-bold text-gray-600">{goalProgress.period_label}</p>
          </div>
          <div className="flex items-center gap-4 mb-3">
            <div className="text-center">
              <p className="text-3xl font-black">{goalProgress.stories_completed}</p>
              <p className="text-xs font-bold text-gray-500">completed</p>
            </div>
            <p className="text-2xl font-black text-gray-300">/</p>
            <div className="text-center">
              <p className="text-3xl font-black">{goalProgress.stories_required}</p>
              <p className="text-xs font-bold text-gray-500">required</p>
            </div>
            <div className="flex-1 ml-4">
              <div className="w-full h-4 rounded-full bg-gray-200 border-2 border-black">
                <div className={`h-full rounded-full transition-all ${goalProgress.is_on_track ? 'bg-emerald-500' : 'bg-red-500'}`} style={{ width: `${goalProgress.completion_percentage}%` }} />
              </div>
              <p className="text-xs font-bold text-gray-500 mt-1">{goalProgress.completion_percentage}% complete · {goalProgress.remaining} remaining</p>
            </div>
          </div>
        </div>
      )}

      {/* Cumulative Time Log */}
      <BrutalCard shadow="lg" data-testid="time-log-section">
        <h3 className="text-xl font-black uppercase mb-4 flex items-center gap-2">
          <Timer size={20} className="text-violet-500" /> Cumulative Time Log
        </h3>
        {timeLogLoading ? (
          <p className="text-gray-500 font-medium py-4 text-center">Loading time data...</p>
        ) : !timeLog || timeLog.total_sessions === 0 ? (
          <EmptyState message="No login sessions recorded yet" />
        ) : (
          <div className="space-y-5">
            {/* Time Summary Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="bg-violet-50 border-4 border-black p-4 brutal-shadow-sm" data-testid="time-total-days">
                <div className="flex items-center gap-2 mb-1">
                  <CalendarDays size={16} className="text-violet-600" />
                  <p className="font-bold text-xs uppercase text-gray-600">Days Active</p>
                </div>
                <p className="text-2xl font-black">{timeLog.total_days_logged_in}</p>
                <p className="text-xs text-gray-500 font-medium">unique days</p>
              </div>
              <div className="bg-violet-50 border-4 border-black p-4 brutal-shadow-sm" data-testid="time-cumulative">
                <div className="flex items-center gap-2 mb-1">
                  <Timer size={16} className="text-violet-600" />
                  <p className="font-bold text-xs uppercase text-gray-600">Total Time</p>
                </div>
                <p className="text-2xl font-black">{timeLog.cumulative_display}</p>
                <p className="text-xs text-gray-500 font-medium">{timeLog.total_sessions} sessions</p>
              </div>
              <div className="bg-violet-50 border-4 border-black p-4 brutal-shadow-sm" data-testid="time-avg-daily">
                <div className="flex items-center gap-2 mb-1">
                  <Activity size={16} className="text-violet-600" />
                  <p className="font-bold text-xs uppercase text-gray-600">Avg / Day</p>
                </div>
                <p className="text-2xl font-black">{timeLog.average_daily_display}</p>
                <p className="text-xs text-gray-500 font-medium">per active day</p>
              </div>
              <div className="bg-violet-50 border-4 border-black p-4 brutal-shadow-sm" data-testid="time-total-sessions">
                <div className="flex items-center gap-2 mb-1">
                  <TrendingUp size={16} className="text-violet-600" />
                  <p className="font-bold text-xs uppercase text-gray-600">Sessions</p>
                </div>
                <p className="text-2xl font-black">{timeLog.total_sessions}</p>
                <p className="text-xs text-gray-500 font-medium">total logins</p>
              </div>
            </div>

            {/* Daily Activity Chart */}
            {timeLog.daily_logs && timeLog.daily_logs.length > 0 && (
              <div>
                <h4 className="font-bold text-sm uppercase text-gray-600 mb-3">Daily Login Hours</h4>
                <div className="h-52" data-testid="time-log-chart">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={timeLog.daily_logs.slice(-30)}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis
                        dataKey="date"
                        tick={{ fontSize: 11, fontWeight: 700 }}
                        tickFormatter={(d) => d.slice(5)}
                      />
                      <YAxis tick={{ fontSize: 11 }} label={{ value: 'Hours', angle: -90, position: 'insideLeft', fontSize: 11, fontWeight: 700 }} />
                      <Tooltip
                        formatter={(v) => [`${v.toFixed(2)} hrs`, 'Time']}
                        labelFormatter={(d) => `Date: ${d}`}
                      />
                      <Bar dataKey="hours" fill="#8b5cf6" stroke="#000" strokeWidth={2} radius={[4, 4, 0, 0]} name="Hours" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}
          </div>
        )}
      </BrutalCard>

      {/* Charts Row */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Vocabulary Mastery Pie */}
        <BrutalCard shadow="lg">
          <h3 className="text-xl font-black uppercase mb-4 flex items-center gap-2">
            <Target size={20} className="text-indigo-500" /> Vocabulary Mastery
          </h3>
          {vocabulary.mastered_count > 0 || vocabulary.biological_target > 0 ? (
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={masteryData} cx="50%" cy="50%" innerRadius={60} outerRadius={90} paddingAngle={4} dataKey="value" label={({ name, value }) => `${name}: ${value}`}>
                    {masteryData.map((_, idx) => (
                      <Cell key={idx} fill={CHART_COLORS[idx]} stroke="#000" strokeWidth={2} />
                    ))}
                  </Pie>
                  <Legend />
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <EmptyState message="No vocabulary data yet" />
          )}
          {vocabulary.mastery_percentage > 0 && (
            <p className="text-center font-bold mt-2">{vocabulary.mastery_percentage}% of biological target</p>
          )}
        </BrutalCard>

        {/* Assessment Accuracy Chart */}
        <BrutalCard shadow="lg">
          <h3 className="text-xl font-black uppercase mb-4 flex items-center gap-2">
            <Award size={20} className="text-amber-500" /> Assessment History
          </h3>
          {assessmentChartData.length > 0 ? (
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={assessmentChartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" tick={{ fontSize: 12, fontWeight: 700 }} />
                  <YAxis domain={[0, 100]} tick={{ fontSize: 12 }} />
                  <Tooltip formatter={(v) => `${v}%`} />
                  <Bar dataKey="accuracy" fill="#6366f1" stroke="#000" strokeWidth={2} radius={[4, 4, 0, 0]} name="Accuracy %" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <EmptyState message="No assessments completed yet" />
          )}
        </BrutalCard>
      </div>

      {/* Stories Section with Per-Story Stats */}
      <BrutalCard shadow="lg">
        <h3 className="text-xl font-black uppercase mb-4 flex items-center gap-2">
          <BookOpen size={20} className="text-emerald-500" /> Story Progress & Statistics
        </h3>
        {narratives.stories.length > 0 ? (
          <div className="space-y-3">
            {narratives.stories.filter(s => s.status !== 'archived' || showArchived).map((story) => (
              <StoryStatsCard
                key={story.id}
                story={story}
                onRead={async () => {
                  try {
                    const res = await narrativeAPI.getById(story.id);
                    setReadingStory(res.data);
                    setReadingChapter(1);
                  } catch (err) {
                    toast.error('Failed to load story');
                  }
                }}
                onArchive={async () => {
                  if (story.status === 'archived') {
                    if (!window.confirm('Restore this story? It will be visible to the student again.')) return;
                    await narrativeAPI.unarchive(story.id);
                    story.status = 'active';
                  } else {
                    if (!window.confirm('Archive this story?\n\n• Hidden from the student\'s story list\n• Data still available for parents, brands & system analytics\n• Can be restored anytime')) return;
                    await narrativeAPI.archive(story.id);
                    story.status = 'archived';
                  }
                  await queryClient.refetchQueries({ queryKey: ['student-progress', studentId] });
                  toast.success(story.status === 'archived' ? 'Story archived — hidden from student. Data preserved for parents & analytics.' : 'Story restored');
                }}
                onDelete={async () => {
                  if (!window.confirm('PERMANENTLY delete this story and all related data? This cannot be undone.')) return;
                  await narrativeAPI.delete(story.id);
                  await queryClient.refetchQueries({ queryKey: ['student-progress', studentId] });
                  toast.success('Story permanently deleted');
                }}
              />
            ))}
            <button onClick={() => setShowArchived(!showArchived)} className="text-xs font-bold text-gray-400 hover:text-gray-600 mt-2">
              {showArchived ? 'Hide archived stories' : `Show archived stories (${narratives.stories.filter(s => s.status === 'archived').length})`}
            </button>
          </div>
        ) : (
          <EmptyState message="No stories generated yet" />
        )}
      </BrutalCard>

      {/* Word Banks & Virtues */}
      <div className="grid md:grid-cols-2 gap-6">
        <BrutalCard shadow="lg">
          <h3 className="text-xl font-black uppercase mb-4">Assigned Word Banks</h3>
          {assigned_banks.length > 0 ? (
            <div className="space-y-2">
              {assigned_banks.map((bank) => (
                <div key={bank.id} className="flex items-center justify-between border-2 border-black p-3 bg-indigo-50">
                  <span className="font-bold">{bank.name}</span>
                  <BrutalBadge variant="indigo" size="sm">{bank.total_tokens} words</BrutalBadge>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 font-medium">No word banks assigned yet</p>
          )}
        </BrutalCard>

        <BrutalCard shadow="lg">
          <h3 className="text-xl font-black uppercase mb-4">Character Education</h3>
          {student.virtues && student.virtues.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {student.virtues.map((virtue, idx) => (
                <BrutalBadge key={idx} variant="emerald" size="lg">{virtue}</BrutalBadge>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 font-medium">No virtues added yet</p>
          )}
        </BrutalCard>
      </div>

      {/* Recently Mastered Words */}
      {vocabulary.recent_mastered.length > 0 && (
        <BrutalCard shadow="lg">
          <h3 className="text-xl font-black uppercase mb-4">Recently Mastered Words</h3>
          <div className="flex flex-wrap gap-2">
            {vocabulary.recent_mastered.map((word, idx) => (
              <span key={idx} className="px-4 py-2 bg-emerald-100 border-4 border-black font-black text-lg brutal-shadow-sm">
                {word}
              </span>
            ))}
          </div>
        </BrutalCard>
      )}

      {/* Story Reader Modal */}
      {readingStory && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white border-4 border-black max-w-3xl w-full mx-4 max-h-[90vh] flex flex-col shadow-[8px_8px_0_0_rgba(0,0,0,1)]">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b-4 border-black bg-indigo-50">
              <div>
                <h3 className="text-xl font-black uppercase">{readingStory.title}</h3>
                <p className="text-sm font-medium text-gray-500">
                  Chapter {readingChapter} of {readingStory.chapters?.length || 0}
                </p>
              </div>
              <button onClick={() => setReadingStory(null)} className="p-1 hover:bg-indigo-100 border-2 border-black">
                <X size={20} />
              </button>
            </div>

            {/* Chapter Content */}
            <div className="flex-1 overflow-y-auto p-6">
              {readingStory.chapters && readingStory.chapters.length > 0 ? (
                <div>
                  <h4 className="text-lg font-black mb-4">
                    {readingStory.chapters[readingChapter - 1]?.title || `Chapter ${readingChapter}`}
                  </h4>
                  <div className="prose max-w-none text-base leading-relaxed whitespace-pre-wrap">
                    {readingStory.chapters[readingChapter - 1]?.content || 'No content available'}
                  </div>
                </div>
              ) : (
                <p className="text-gray-500 font-medium text-center py-8">No chapters available</p>
              )}
            </div>

            {/* Chapter Navigation */}
            {readingStory.chapters && readingStory.chapters.length > 1 && (
              <div className="flex items-center justify-between p-4 border-t-4 border-black bg-gray-50">
                <BrutalButton
                  variant="default"
                  size="sm"
                  onClick={() => setReadingChapter((c) => Math.max(1, c - 1))}
                  disabled={readingChapter <= 1}
                >
                  Previous
                </BrutalButton>
                <div className="flex gap-1">
                  {readingStory.chapters.map((_, idx) => (
                    <button
                      key={idx}
                      onClick={() => setReadingChapter(idx + 1)}
                      className={`w-8 h-8 text-xs font-black border-2 border-black ${
                        readingChapter === idx + 1
                          ? 'bg-indigo-500 text-white'
                          : 'bg-white hover:bg-indigo-50'
                      }`}
                    >
                      {idx + 1}
                    </button>
                  ))}
                </div>
                <BrutalButton
                  variant="default"
                  size="sm"
                  onClick={() => setReadingChapter((c) => Math.min(readingStory.chapters.length, c + 1))}
                  disabled={readingChapter >= readingStory.chapters.length}
                >
                  Next
                </BrutalButton>
              </div>
            )}
          </div>
        </div>
      )}

      <GuardianWritingPanel studentId={studentId} />
      <ResearchConsentBox studentId={studentId} />
    </div>
  );
};

const ProgressTab = () => {
  const { user } = useAuth();
  const [selectedStudentId, setSelectedStudentId] = useState(null);

  const { data: students = [], isLoading } = useQuery({
    queryKey: ['students', user?.id],
    queryFn: async () => {
      const res = await studentAPI.getAll(user?.id);
      return res.data;
    },
    enabled: !!user?.id,
  });

  if (selectedStudentId) {
    return <StudentProgressDetail studentId={selectedStudentId} onBack={() => setSelectedStudentId(null)} />;
  }

  if (isLoading) return <div className="text-center py-12 text-2xl font-bold">Loading...</div>;

  return (
    <div className="space-y-6" data-testid="progress-tab">
      <h2 className="text-3xl font-black uppercase">Student Progress</h2>
      <p className="text-lg font-medium text-gray-600">Select a student to view detailed analytics</p>

      {students.length === 0 ? (
        <BrutalCard shadow="xl" className="text-center py-12">
          <TrendingUp size={48} className="mx-auto mb-4 text-gray-400" />
          <p className="text-2xl font-bold mb-2">No students yet</p>
          <p className="text-lg text-gray-500">Add students from the Students tab to track their progress</p>
        </BrutalCard>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {students.map((student) => (
            <BrutalCard
              key={student.id}
              shadow="lg"
              hover
              className="cursor-pointer transition-transform hover:-translate-y-1"
              onClick={() => setSelectedStudentId(student.id)}
              data-testid={`progress-student-card-${student.id}`}
            >
              <div className="mb-4">
                <h3 className="text-2xl font-black uppercase">{student.full_name}</h3>
                <p className="text-sm font-medium text-gray-500">
                  {student.age ? `Age ${student.age}` : ''}{student.grade_level ? ` · Grade ${student.grade_level}` : ''}
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4 border-t-4 border-black pt-4">
                <div>
                  <p className="text-xs font-bold uppercase text-gray-600">Mastered</p>
                  <p className="text-2xl font-black">{student.mastered_tokens?.length || 0}</p>
                </div>
                <div>
                  <p className="text-xs font-bold uppercase text-gray-600">Word Banks</p>
                  <p className="text-2xl font-black">{student.assigned_banks?.length || 0}</p>
                </div>
                <div>
                  <p className="text-xs font-bold uppercase text-gray-600">Score</p>
                  <p className="text-2xl font-black">{student.agentic_reach_score || 0}</p>
                </div>
                <div>
                  <p className="text-xs font-bold uppercase text-gray-600">Target</p>
                  <p className="text-2xl font-black">{student.biological_target || 0}</p>
                </div>
              </div>

              {student.reading_goal && (
                <div className="mt-3 pt-3 border-t-2 border-gray-200">
                  <div className="flex items-center gap-2">
                    <Target size={14} className="text-violet-500" />
                    <span className="text-xs font-bold text-gray-600">
                      Goal: {student.reading_goal.stories_required} stories / {student.reading_goal.period}
                    </span>
                  </div>
                </div>
              )}

              <div className="mt-4">
                <BrutalButton variant="indigo" fullWidth size="sm" className="flex items-center justify-center gap-2" data-testid={`view-progress-btn-${student.id}`}>
                  <TrendingUp size={16} /> View Progress
                </BrutalButton>
              </div>
            </BrutalCard>
          ))}
        </div>
      )}
    </div>
  );
};

export default ProgressTab;
